import React, { useEffect, useState, useContext, useRef, useCallback, useMemo } from "react";
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from "react-router-dom";
import { FormAlert } from '../components/form';
import FacturasCreatePanel from './facturas/FacturasCreatePanel';
import FacturasListQuickCta from './facturas/FacturasListQuickCta';
import FacturasListPanel from './facturas/FacturasListPanel';
import {
  amountInArs,
  isQuickSale,
} from './facturas/facturasPageUtils';
import { AuthContext } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { ThemeContext } from '../context/ThemeContext';
import MoneyDisplay from '../components/MoneyDisplay';
import { canAccessProfesionalFeatures } from '../utils/profesionalAccess';
import { formatMoney } from '../utils/money';
import { downloadTabularExport } from '../utils/downloadExport';
import {
  GESTION_TIPOS,
  agingLabel,
  agingTone,
  cobranzaWhatsappText,
  gestionesOf,
  isFullyPaid,
  isPendiente,
  matchesAging,
  montoPagado,
  pagosOf,
  saldoPendiente,
  whatsappHref,
  invoiceId,
  sameInvoiceId,
  normalizeInvoiceFromApi,
  patchInvoiceInList,
} from '../utils/cobranzas';
import RecurringList from '../components/RecurringList';
import { defaultNextRun, runDueRecurring } from '../utils/recurring';
import { cloneInvoiceForForm } from '../utils/duplicar';
import { isOperationalWriteDisabled } from '../utils/writeAccess';
import OnboardingContinueAction from '../components/OnboardingContinueAction';
import { isFromOnboarding } from '../utils/onboarding';
import { whatsappOptionsFromPrefs } from '../utils/whatsappTemplates';
import { usePageFeedback } from '../hooks/usePageFeedback';
import { lockAppScroll, unlockAppScroll } from '../utils/mainScroll';
import PageFeedbackBlock from '../components/PageFeedbackBlock';
import { runModalClientAction } from '../utils/modalActionFeedback';

// Hook personalizado para debounce
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return debouncedValue;
};


const FacturaDetalleModal = ({
  factura,
  onClose,
  modoOscuro,
  onEnviarRecordatorio,
  onEnviarEmail,
  onAddGestion,
  onAddPago,
  hasProfesional = false,
  readOnly = false,
  whatsappOptions = null,
}) => {
  const [gestionTipo, setGestionTipo] = React.useState('llamado');
  const [gestionTexto, setGestionTexto] = React.useState('');
  const [promesaFecha, setPromesaFecha] = React.useState('');
  const [savingGestion, setSavingGestion] = React.useState(false);
  const [pagoMonto, setPagoMonto] = React.useState('');
  const [pagoFecha, setPagoFecha] = React.useState(() => new Date().toISOString().slice(0, 10));
  const [pagoNota, setPagoNota] = React.useState('');
  const [savingPago, setSavingPago] = React.useState(false);
  const [modalFeedback, setModalFeedback] = React.useState(null);

  React.useEffect(() => {
    setModalFeedback(null);
  }, [factura?._id]);

  React.useEffect(() => {
    if (!factura) return undefined;
    lockAppScroll();
    return () => unlockAppScroll();
  }, [factura]);

  if (!factura) return null;
  
  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-700';
  const divide = modoOscuro ? 'border-gray-700' : 'border-gray-200';
  const cardInner = modoOscuro ? 'bg-gray-800' : 'bg-gray-50';
  const btnPrimary = modoOscuro ? 'bg-[#1F80FF] hover:bg-blue-600 text-white' : 'bg-[#1F80FF] hover:bg-blue-700 text-white';
  const btnOut = modoOscuro ? 'border-gray-600 text-white hover:bg-gray-700' : 'border-gray-300 text-gray-900 hover:bg-gray-50';
  const inputClass = modoOscuro ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900';
  const pendiente = isPendiente(factura);
  const puedeRecordatorio = hasProfesional && pendiente && factura.email;
  const puedeEmail = !!factura.email;
  const waHref = whatsappHref(factura.telefono, cobranzaWhatsappText(factura, whatsappOptions));
  const agingTxt = pendiente ? agingLabel(factura) : null;
  const gestiones = gestionesOf(factura);
  const pagos = pagosOf(factura);
  const saldo = saldoPendiente(factura);

  const handleSaveGestion = async (tipo = gestionTipo, extra = {}) => {
    if (!onAddGestion) return;
    setSavingGestion(true);
    setModalFeedback(null);
    try {
      const result = await onAddGestion(invoiceId(factura), {
        tipo,
        texto: extra.texto != null ? extra.texto : gestionTexto,
        promesaFecha: tipo === 'promesa' ? (extra.promesaFecha || promesaFecha) : null,
      });
      setGestionTexto('');
      setPromesaFecha('');
      if (result?.message) {
        setModalFeedback({
          type: result.ok ? 'success' : 'error',
          text: result.message,
        });
      }
    } catch (err) {
      setModalFeedback({
        type: 'error',
        text: err.message || 'Error al guardar la gestión',
      });
    } finally {
      setSavingGestion(false);
    }
  };

  const handleDescargarAdjunto = () => {
    if (!factura._id || !factura.adjunto) return;
    window.open(`/api/invoices/${factura._id}/adjunto`, '_blank');
  };

  const handleEmailDesdeModal = () => {
    if (!onEnviarEmail) return;
    runModalClientAction(setModalFeedback, onEnviarEmail(factura._id));
  };

  const handleRecordatorioDesdeModal = () => {
    if (!onEnviarRecordatorio) return;
    runModalClientAction(setModalFeedback, onEnviarRecordatorio(factura._id));
  };

  const modal = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-hidden overscroll-none"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="factura-detalle-title"
    >
      <div
        className={`rounded-2xl shadow-2xl max-w-2xl w-full max-h-[min(90dvh,calc(100dvh-2rem))] flex flex-col overflow-hidden relative border ${box}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header del modal */}
        <div className={`border-b p-5 shrink-0 ${divide}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white ${modoOscuro ? 'bg-gray-700' : 'bg-gray-900'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h2 id="factura-detalle-title" className={`text-lg font-semibold ${textPri}`}>Detalle del registro de venta</h2>
                <p className={`text-xs ${modoOscuro ? 'text-gray-400' : 'text-gray-500'}`}>Registro #{factura.numero}</p>
              </div>
            </div>
            <button
              onClick={onClose} 
              className={modoOscuro ? 'w-8 h-8 bg-gray-700 hover:bg-gray-600 rounded-full flex items-center justify-center transition-colors text-white' : 'w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors text-gray-700'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Contenido del modal (único scroll) */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-6 space-y-6">
          {modalFeedback && (
            <FormAlert type={modalFeedback.type}>{modalFeedback.text}</FormAlert>
          )}
          {/* Información principal */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className={`p-4 rounded-xl border ${box}`}>
                <h3 className={`font-semibold mb-3 flex items-center gap-2 text-sm ${textPri}`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Información del Cliente
                </h3>
                <div className={`space-y-2 text-xs sm:text-sm ${textSec}`}>
                  <div><span className="font-medium">Razón social:</span> {factura.razonSocial}</div>
                  <div><span className="font-medium">País:</span> {factura.pais}</div>
                  <div><span className="font-medium">Email:</span> {factura.email}</div>
                  <div><span className="font-medium">Domicilio:</span> {factura.domicilio}</div>
                  <div><span className="font-medium">Localidad:</span> {factura.localidad}</div>
                  <div><span className="font-medium">Teléfono:</span> {factura.telefono}</div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className={`p-4 rounded-xl border ${box}`}>
                <h3 className={`font-semibold mb-3 flex items-center gap-2 text-sm ${textPri}`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Detalles del registro
                </h3>
                <div className={`space-y-3 text-xs sm:text-sm ${textSec}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-medium">Número:</span>
                    <div className={`rounded-lg px-3 py-1 ${modoOscuro ? 'bg-gray-700' : 'bg-gray-900'}`}>
                      <span className="text-white font-mono font-semibold text-xs sm:text-sm tracking-wide">#{factura.numero}</span>
                    </div>
                  </div>
                  <div><span className="font-medium">Fecha:</span> {factura.fecha}</div>
                  {factura.vencimiento && (
                    <div><span className="font-medium">Vencimiento:</span> {String(factura.vencimiento).slice(0, 10)}</div>
                  )}
                  {agingTxt && (
                    <div>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${agingTone(factura, modoOscuro)}`}>
                        {agingTxt}
                      </span>
                    </div>
                  )}
                  <div><span className="font-medium">Tipo:</span> {factura.tipo}</div>
                  <div className={`pt-2 border-t ${divide}`}>
                    <MoneyDisplay
                      amount={factura.total}
                      currency={factura.moneda || 'ARS'}
                      arsAmount={factura.montoArs}
                      tipoCambio={factura.tipoCambio}
                      modoOscuro={modoOscuro}
                      align="left"
                      size="lg"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Conceptos */}
          <div className={`p-4 rounded-xl border ${box}`}>
            <h3 className={`font-semibold mb-3 flex items-center gap-2 text-sm ${textPri}`}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Conceptos
            </h3>
            <div className="space-y-2">
              {factura.conceptos && factura.conceptos.map((c, i) => (
                <div key={i} className={`flex justify-between items-center py-2 px-3 rounded-lg ${cardInner}`}>
                  <span className={`text-sm ${modoOscuro ? 'text-gray-200' : 'text-gray-800'}`}>{c.detalle}</span>
                  <span className={`font-semibold text-sm ${textPri}`}>
                    {formatMoney(c.importe, factura.moneda || 'ARS')}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Adjunto si existe */}
          {factura.adjunto && (
            <div className={`p-4 rounded-xl border ${box}`}>
              <h3 className={`font-semibold mb-3 flex items-center gap-2 text-sm ${textPri}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                Archivo Adjunto
              </h3>
              <div className="flex items-center justify-between gap-3">
                <span className={`text-sm font-medium truncate max-w-xs ${textSec}`}>{factura.adjunto}</span>
                <button
                  className={modoOscuro ? 'px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors text-sm font-semibold' : 'px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-black transition-colors text-sm font-semibold'}
                  onClick={handleDescargarAdjunto}
                >
                  Descargar
                </button>
              </div>
          </div>
        )}

          {!readOnly && (
          <div className={`p-4 rounded-xl border ${box}`}>
            <h3 className={`font-semibold mb-3 flex items-center gap-2 text-sm ${textPri}`}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Enviar al cliente
            </h3>
            <div className="flex flex-wrap gap-2">
              {onEnviarEmail && (
                <button
                  type="button"
                  disabled={!puedeEmail}
                  onClick={handleEmailDesdeModal}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${puedeEmail ? btnOut : 'opacity-50 cursor-not-allowed border-gray-500 text-gray-500'}`}
                  title={puedeEmail ? 'Enviar comprobante por email' : 'Este registro no tiene email de cliente'}
                >
                  Enviar comprobante
                </button>
              )}
              {onEnviarRecordatorio && hasProfesional && (
                <button
                  type="button"
                  disabled={!puedeRecordatorio}
                  onClick={handleRecordatorioDesdeModal}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${puedeRecordatorio ? btnPrimary : 'opacity-50 cursor-not-allowed border-gray-500 text-gray-500'}`}
                  title={puedeRecordatorio ? 'Enviar recordatorio de cobro por email' : !pendiente ? 'Ya está pagado' : 'Este registro no tiene email de cliente'}
                >
                  Recordatorio de cobro
                </button>
              )}
              {pendiente && (
                <button
                  type="button"
                  disabled={!waHref}
                  onClick={() => {
                    if (!waHref) return;
                    window.open(waHref, '_blank', 'noopener,noreferrer');
                    handleSaveGestion('whatsapp', { texto: 'Mensaje de WhatsApp abierto' });
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${waHref ? btnOut : 'opacity-50 cursor-not-allowed border-gray-500 text-gray-500'}`}
                  title={waHref ? 'Abrir WhatsApp con un texto armado' : 'Este cliente no tiene teléfono'}
                >
                  WhatsApp
                </button>
              )}
            </div>
          </div>
          )}

          <div className={`p-4 rounded-xl border ${box}`}>
            <h3 className={`font-semibold mb-3 text-sm ${textPri}`}>Pagos</h3>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className={`px-3 py-2 rounded-lg ${cardInner}`}>
                <div className={`text-[10px] uppercase ${modoOscuro ? 'text-gray-400' : 'text-gray-500'}`}>Cobrado</div>
                <div className={`text-sm font-semibold ${textPri}`}>{formatMoney(montoPagado(factura), factura.moneda || 'ARS')}</div>
              </div>
              <div className={`px-3 py-2 rounded-lg ${cardInner}`}>
                <div className={`text-[10px] uppercase ${modoOscuro ? 'text-gray-400' : 'text-gray-500'}`}>Saldo</div>
                <div className={`text-sm font-semibold ${textPri}`}>{formatMoney(saldo, factura.moneda || 'ARS')}</div>
              </div>
            </div>
            {pendiente && onAddPago && !readOnly && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={pagoMonto}
                  onChange={(e) => setPagoMonto(e.target.value)}
                  placeholder={`Hasta ${saldo}`}
                  className={`px-3 py-2 rounded-lg border text-sm ${inputClass}`}
                />
                <input
                  type="date"
                  value={pagoFecha}
                  onChange={(e) => setPagoFecha(e.target.value)}
                  className={`px-3 py-2 rounded-lg border text-sm ${inputClass}`}
                />
                <input
                  type="text"
                  value={pagoNota}
                  onChange={(e) => setPagoNota(e.target.value)}
                  placeholder="Nota (opcional)"
                  className={`px-3 py-2 rounded-lg border text-sm ${inputClass}`}
                />
                <button
                  type="button"
                  disabled={savingPago}
                  onClick={async () => {
                    setSavingPago(true);
                    try {
                      const result = await onAddPago(invoiceId(factura), {
                        monto: Number(pagoMonto),
                        fecha: pagoFecha,
                        nota: pagoNota,
                      });
                      setPagoMonto('');
                      setPagoNota('');
                      if (result?.message) {
                        setModalFeedback({
                          type: result.ok ? 'success' : 'error',
                          text: result.message,
                        });
                      }
                    } catch (err) {
                      const msg = err.message || 'No se pudo registrar el pago.';
                      setModalFeedback({ type: 'error', text: msg });
                    } finally {
                      setSavingPago(false);
                    }
                  }}
                  className={`sm:col-span-3 px-3 py-1.5 rounded-lg text-xs font-semibold ${btnPrimary} disabled:opacity-50`}
                >
                  {savingPago ? 'Registrando…' : 'Registrar pago'}
                </button>
              </div>
            )}
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {pagos.length === 0 ? (
                <p className={`text-xs ${modoOscuro ? 'text-gray-400' : 'text-gray-500'}`}>
                  {pendiente ? 'Todavía no hay pagos. Registrá un parcial o marcá pagada.' : 'Pagada.'}
                </p>
              ) : pagos.map((p) => (
                <div key={p.id || p.createdAt} className={`px-3 py-2 rounded-lg text-xs ${cardInner}`}>
                  <div className="flex justify-between gap-2">
                    <span className={`font-semibold ${textPri}`}>{formatMoney(p.monto, factura.moneda || 'ARS')}</span>
                    <span className={modoOscuro ? 'text-gray-400' : 'text-gray-500'}>{String(p.fecha || '').slice(0, 10)}</span>
                  </div>
                  {p.nota && <p className={`mt-0.5 ${textSec}`}>{p.nota}</p>}
                </div>
              ))}
            </div>
          </div>

          {pendiente && !readOnly && (
            <div className={`p-4 rounded-xl border ${box}`}>
              <h3 className={`font-semibold mb-3 text-sm ${textPri}`}>Seguimiento de cobranza</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                <select
                  value={gestionTipo}
                  onChange={(e) => setGestionTipo(e.target.value)}
                  className={`px-3 py-2 rounded-lg border text-sm ${inputClass}`}
                >
                  {GESTION_TIPOS.map((t) => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
                {gestionTipo === 'promesa' && (
                  <input
                    type="date"
                    value={promesaFecha}
                    onChange={(e) => setPromesaFecha(e.target.value)}
                    className={`px-3 py-2 rounded-lg border text-sm ${inputClass}`}
                  />
                )}
              </div>
              <textarea
                value={gestionTexto}
                onChange={(e) => setGestionTexto(e.target.value)}
                rows={2}
                placeholder="Qué pasó: lo llamé, prometió el viernes…"
                className={`w-full px-3 py-2 rounded-lg border text-sm mb-2 ${inputClass}`}
              />
              <button
                type="button"
                disabled={savingGestion}
                onClick={() => handleSaveGestion()}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${btnPrimary} disabled:opacity-50`}
              >
                {savingGestion ? 'Guardando…' : 'Registrar gestión'}
              </button>
              <div className="mt-3 space-y-2 max-h-40 overflow-y-auto">
                {gestiones.length === 0 ? (
                  <p className={`text-xs ${modoOscuro ? 'text-gray-400' : 'text-gray-500'}`}>Todavía no hay gestiones.</p>
                ) : gestiones.map((g) => (
                  <div key={g.id || g.createdAt} className={`px-3 py-2 rounded-lg text-xs ${cardInner}`}>
                    <div className="flex justify-between gap-2">
                      <span className={`font-semibold ${textPri}`}>
                        {GESTION_TIPOS.find((t) => t.id === g.tipo)?.label || g.tipo}
                      </span>
                      <span className={modoOscuro ? 'text-gray-400' : 'text-gray-500'}>
                        {String(g.fecha || g.createdAt || '').slice(0, 10)}
                      </span>
                    </div>
                    {g.texto && <p className={`mt-0.5 ${textSec}`}>{g.texto}</p>}
                    {g.promesaFecha && (
                      <p className={modoOscuro ? 'text-amber-300' : 'text-amber-700'}>Prometió: {String(g.promesaFecha).slice(0, 10)}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

const Facturas = () => {
  const { user, token } = useContext(AuthContext);
  const { modoOscuro } = useContext(ThemeContext);
  const { secureFetch } = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Hook de permisos de administración
  // const { canAccessABM, getABMAccessLevel, userPlan } = useAdminPermissions();

  const [facturas, setFacturas] = useState([]);
  const [facturasFiltradas, setFacturasFiltradas] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [detalle, setDetalle] = useState(null);
  const detalleRef = useRef(null);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState(null);
  const {
    success,
    error,
    errorCode,
    clearFeedback,
    clearError,
    setSuccess,
    setError,
    reportSuccess,
    reportError,
    reportActionResult,
  } = usePageFeedback();

  const publishActionResult = useCallback(
    (result) => reportActionResult(result, { scroll: !detalleRef.current }),
    [reportActionResult],
  );

  useEffect(() => {
    detalleRef.current = detalle;
  }, [detalle]);

  // Determinar tab inicial según query param (?tab=crear) para mejorar el flujo desde Inicio
  const searchParams = new URLSearchParams(location.search);
  const onboardingFlow = isFromOnboarding(location.search);
  const initialTab = searchParams.get('tab') === 'crear' ? 'crear' : 'listado';
  const initialEstado = ['todas', 'pagada', 'impaga'].includes(searchParams.get('estado'))
    ? searchParams.get('estado')
    : 'todas';
  const agingParam = searchParams.get('aging');
  const initialAging = ['todas', 'vigente', '1-30', '31-60', '61+', 'vencidas'].includes(agingParam)
    ? agingParam
    : (initialEstado === 'impaga' && agingParam === 'vencidas' ? 'vencidas' : 'todas');
  const [tab, setTab] = useState(initialTab);
  const [createMode, setCreateMode] = useState(
    searchParams.get('modo') === 'rapido' ? 'quick' : 'complete'
  );
  const [, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState(initialEstado);
  const [filtroAging, setFiltroAging] = useState(initialAging);
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');
  const [filtroCliente, setFiltroCliente] = useState((searchParams.get('cliente') || '').trim());
  const [nuevaFacturaId, setNuevaFacturaId] = useState(null);
  const [quickFormResetKey, setQuickFormResetKey] = useState(0);
  const [lastCreatedSale, setLastCreatedSale] = useState(null);
  const [nuevaAdjunto, setNuevaAdjunto] = useState(null);
  const [nuevaAdjuntoInfo, setNuevaAdjuntoInfo] = useState(null);
  const nuevaFileInputRef = useRef();
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [bulkRemindersSending, setBulkRemindersSending] = useState(false);
  const [importingMp, setImportingMp] = useState(false);
  const mpFileInputRef = useRef(null);
  const fetchInFlightRef = useRef(false);
  const fetchPendingRef = useRef(false);
  const fetchGenerationRef = useRef(0);
  const hasProfesional = canAccessProfesionalFeatures(user);
  const readOnly = isOperationalWriteDisabled(user);
  const [collectionPrefs, setCollectionPrefs] = useState(null);

  useEffect(() => {
    if (!hasProfesional || !token) return;
    const loadCollectionPrefs = async () => {
      try {
        const res = await fetch('/api/notifications/preferences', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setCollectionPrefs(data);
      } catch {
        // silencioso
      }
    };
    loadCollectionPrefs();
  }, [hasProfesional, token]);

  const whatsappOptions = useMemo(
    () => whatsappOptionsFromPrefs(collectionPrefs, user),
    [collectionPrefs, user],
  );

  useEffect(() => {
    if (readOnly && tab !== 'listado') {
      setTab('listado');
      navigate('/facturas', { replace: true });
    }
  }, [readOnly, tab, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedTab = params.get('tab');
    if (readOnly) return;

    if (requestedTab === 'crear') {
      setCreateMode(params.get('modo') === 'rapido' ? 'quick' : 'complete');
      setTab('crear');
    } else if (requestedTab === 'recurrentes') {
      setEditId(null);
      setEditData(null);
      setTab('recurrentes');
    } else {
      setEditId(null);
      setEditData(null);
      setTab('listado');
    }
  }, [location.search, readOnly]);

  useEffect(() => {
    if (!detalle) return;
    const fresh = facturas.find((f) => sameInvoiceId(f, detalle));
    if (!fresh || fresh === detalle) return;
    if (
      fresh.estadoPago !== detalle.estadoPago
      || montoPagado(fresh) !== montoPagado(detalle)
      || saldoPendiente(fresh) !== saldoPendiente(detalle)
      || pagosOf(fresh).length !== pagosOf(detalle).length
    ) {
      setDetalle(fresh);
    }
  }, [facturas, detalle]);

  // Debounce para la búsqueda
  const debouncedBusqueda = useDebounce(busqueda, 300);

  const impagasElegibles = useMemo(
    () => facturas.filter((f) => isPendiente(f) && String(f.email || '').trim()),
    [facturas]
  );

  const totalesPorMoneda = useMemo(() => {
    const map = {};
    facturasFiltradas.forEach((f) => {
      const m = (f.moneda || 'ARS').toUpperCase();
      map[m] = (map[m] || 0) + (Number(f.total) || 0);
    });
    return map;
  }, [facturasFiltradas]);

  const totalEquivalenteArs = useMemo(
    () => facturasFiltradas.reduce((sum, f) => sum + amountInArs(f), 0),
    [facturasFiltradas]
  );

  const kpis = useMemo(() => {
    let cobrado = 0;
    let pendiente = 0;
    const clientes = new Set();
    facturasFiltradas.forEach((f) => {
      const ars = amountInArs(f);
      const total = Number(f.total) || 0;
      const ratio = total > 0 ? montoPagado(f) / total : (isFullyPaid(f) ? 1 : 0);
      cobrado += ars * ratio;
      pendiente += ars * (1 - ratio);
      if (f.razonSocial) clientes.add(String(f.razonSocial).trim());
    });
    return {
      cantidad: facturasFiltradas.length,
      cobrado,
      pendiente,
      clientes: clientes.size,
    };
  }, [facturasFiltradas]);

  const fetchFacturas = useCallback(async () => {
    if (!user || !token) return;

    if (fetchInFlightRef.current) {
      fetchPendingRef.current = true;
      return;
    }

    fetchInFlightRef.current = true;
    const generation = fetchGenerationRef.current + 1;
    fetchGenerationRef.current = generation;
    setIsFetching(true);
    setLoading(true);
    clearError();
    try {
      // Obtener ID del usuario para filtrar datos
      const userId = user?.id || user?._id;
      const empresaId = user?.empresaId || user?.empresa?.id;
      
      if (!userId) {
        throw new Error('No se pudo identificar al usuario');
      }
      
      // Construir URL con filtros de usuario - FORZAR SIEMPRE
      const invoicesUrl = `/api/invoices?userId=${userId}&forceUserFilter=true&preventGlobalData=true&userSpecificOnly=true`;
      
      // Headers personalizados para forzar filtro de usuario
      const userSpecificHeaders = {
        'X-Force-User-Filter': 'true',
        'X-Prevent-Global-Data': 'true',
        'X-User-Specific-Only': 'true'
      };
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 FACTURAS: Obteniendo facturas para usuario:', {
          user: user?.email,
          userId,
          empresaId,
          url: invoicesUrl
        });
      }
      
      const res = await secureFetch(invoicesUrl, { headers: userSpecificHeaders });
      if (!res.ok) throw new Error("Error al obtener facturas");
      const raw = await res.json();
      if (generation !== fetchGenerationRef.current) return;
      const data = Array.isArray(raw) ? raw.map(normalizeInvoiceFromApi) : raw;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ FACTURAS: Datos recibidos y validados:', {
          cantidad: data.length,
          usuario: user?.email
        });
      }
      
      setFacturas(data);
    } catch (err) {
      reportError(err.message || 'Error de conexión');
      console.error('❌ FACTURAS: Error al cargar facturas:', err);
    } finally {
      setLoading(false);
      setIsFetching(false);
      fetchInFlightRef.current = false;
      if (fetchPendingRef.current) {
        fetchPendingRef.current = false;
        fetchFacturas();
      }
    }
  }, [user, token, secureFetch, clearError, reportError]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      const due = await runDueRecurring(secureFetch);
      if (cancelled) return;
      await fetchFacturas();
      if (due.generated > 0) {
        reportSuccess(due.generated === 1
          ? 'Se cargó 1 venta recurrente.'
          : `Se cargaron ${due.generated} ventas recurrentes.`);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const estado = params.get('estado');
    if (['todas', 'pagada', 'impaga'].includes(estado)) setFiltroEstado(estado);
    const aging = params.get('aging');
    if (['todas', 'vigente', '1-30', '31-60', '61+', 'vencidas'].includes(aging)) {
      setFiltroAging(aging);
      if (aging === 'vencidas') setFiltroEstado('impaga');
    }
  }, [location.search]);

  // Clientes únicos para filtro (razonSocial)
  const clientesUnicos = useMemo(() => {
    const set = new Set();
    facturas.forEach(f => {
      const name = (f.razonSocial || '').trim();
      if (name) set.add(name);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [facturas]);

  useEffect(() => {
    let filtradas = facturas;
    if (filtroEstado === 'pagada') {
      filtradas = filtradas.filter((f) => isFullyPaid(f));
    } else if (filtroEstado === 'impaga') {
      filtradas = filtradas.filter((f) => isPendiente(f));
    }
    if (filtroEstado === 'impaga' && filtroAging && filtroAging !== 'todas') {
      filtradas = filtradas.filter((f) => matchesAging(f, filtroAging));
    }
    if (filtroCategoria) {
      filtradas = filtradas.filter(f => (f.categoria || '') === filtroCategoria);
    }
    if (filtroCliente) {
      filtradas = filtradas.filter(f => (f.razonSocial || '').trim() === filtroCliente);
    }
    if (filtroFechaDesde) {
      const desde = new Date(filtroFechaDesde);
      desde.setHours(0, 0, 0, 0);
      filtradas = filtradas.filter(f => new Date(f.fecha) >= desde);
    }
    if (filtroFechaHasta) {
      const hasta = new Date(filtroFechaHasta);
      hasta.setHours(23, 59, 59, 999);
      filtradas = filtradas.filter(f => new Date(f.fecha) <= hasta);
    }
    if (debouncedBusqueda.trim()) {
      filtradas = filtradas.filter(factura =>
        factura.numero.toString().toLowerCase().includes(debouncedBusqueda.toLowerCase()) ||
        (factura.razonSocial || '').toLowerCase().includes(debouncedBusqueda.toLowerCase()) ||
        (factura.pais || '').toLowerCase().includes(debouncedBusqueda.toLowerCase())
      );
    }
    // Ordenar de mayor a menor por número (numérico robusto), y por fecha descendente si hay empate o error
    filtradas = filtradas.slice().sort((a, b) => {
      const numA = parseInt(a.numero, 10);
      const numB = parseInt(b.numero, 10);
      if (!isNaN(numA) && !isNaN(numB)) {
        if (numB !== numA) return numB - numA;
      } else if (!isNaN(numA)) {
        return -1;
      } else if (!isNaN(numB)) {
        return 1;
      }
      const fechaA = new Date(a.fecha);
      const fechaB = new Date(b.fecha);
      return fechaB - fechaA;
    });
    setFacturasFiltradas(filtradas);
  }, [facturas, filtroEstado, filtroAging, filtroCategoria, filtroCliente, filtroFechaDesde, filtroFechaHasta, debouncedBusqueda]);

  // Filtrado se realiza por debounce en useEffect; función auxiliar removida por no uso

  const handleBusqueda = (e) => {
    const valor = e.target.value;
    setBusqueda(valor);
    // El filtrado se hace automáticamente con debounce en el useEffect
  };

  const openDetalle = useCallback((factura) => {
    if (!factura) return;
    const fresh = facturas.find((f) => sameInvoiceId(f, factura)) || factura;
    setDetalle(fresh);
  }, [facturas]);

  const goToCreate = (mode = 'complete') => {
    setEditId(null);
    setEditData(null);
    setCreateMode(mode);
    setLastCreatedSale(null);
    clearFeedback();
    setTab('crear');
    navigate(`/facturas?tab=crear&modo=${mode === 'complete' ? 'completo' : 'rapido'}`);
  };

  const switchMainTab = (nextTab) => {
    if (nextTab === tab) return;
    if (nextTab === 'crear') {
      goToCreate('complete');
      return;
    }
    setEditId(null);
    setEditData(null);
    setTab(nextTab);
    navigate(nextTab === 'recurrentes' ? '/facturas?tab=recurrentes' : '/facturas');
  };

  const registrarActividad = async (accion, detalles) => {
    try {
      await secureFetch('/api/activity', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: accion, details: detalles })
      });
    } catch (err) {}
  };

  // Utilidad para mostrar cambios entre dos objetos factura
  const obtenerCambiosFactura = (antes, despues) => {
    if (!antes || !despues) return '';
    const campos = [
      { key: 'numero', label: 'Número' },
      { key: 'razonSocial', label: 'Razón social' },
      { key: 'fecha', label: 'Fecha' },
      { key: 'tipo', label: 'Tipo' },
      { key: 'total', label: 'Total' },
      { key: 'pais', label: 'País' },
      { key: 'email', label: 'Email' },
      { key: 'domicilio', label: 'Domicilio' },
      { key: 'localidad', label: 'Localidad' },
      { key: 'telefono', label: 'Teléfono' }
    ];
    let cambios = [];
    campos.forEach(({key, label}) => {
      if (antes[key] !== despues[key]) {
        cambios.push(`• ${label}: ${antes[key] || '-'} → ${despues[key] || '-'}`);
      }
    });
    // Conceptos
    if (JSON.stringify(antes.conceptos) !== JSON.stringify(despues.conceptos)) {
      cambios.push('• Conceptos: Modificados');
    }
    return cambios.length ? cambios.join('\n') : 'Sin cambios relevantes';
  };

  const handleEliminar = async (id) => {
    if (!window.confirm("¿Seguro que deseas eliminar este registro de venta?")) return;
    setLoading(true);
    clearFeedback();
    try {
      const facturaEliminada = facturas.find(f => f._id === id);
      const res = await secureFetch(`/api/invoices/${id}`, {
        method: "DELETE"
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Error al eliminar factura");
      reportSuccess('Registro eliminado correctamente.');
      if (editId === id) {
        setEditId(null);
        setEditData(null);
      }
      registrarActividad(
        'Eliminar registro',
        `Registro N°${facturaEliminada?.numero} eliminado\n• Cliente: ${facturaEliminada?.razonSocial}\n• Total: $${facturaEliminada?.total}`
      );
      fetchFacturas();
    } catch (err) {
      reportError(err.message || 'Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = idx => {
    const factura = facturasFiltradas[idx];
    setEditId(factura._id);
    setEditData({ ...factura });
    setCreateMode('complete');
    setSuccess("");
    setTab('crear');
    navigate('/facturas?tab=crear&modo=completo');
  };

  const handleDuplicar = (factura) => {
    if (!factura) return;
    setEditId(null);
    setEditData(cloneInvoiceForForm(factura));
    setCreateMode('complete');
    reportSuccess('Revisá la copia y guardá. El número y la fecha se renuevan.');
    setTab('crear');
    navigate('/facturas?tab=crear&modo=completo');
  };

  const handleDuplicarUltima = () => {
    if (!facturas.length) return;
    const ultima = [...facturas].sort((a, b) => String(b.fecha || '').localeCompare(String(a.fecha || ''))
      || Number(b.numero) - Number(a.numero))[0];
    handleDuplicar(ultima);
  };

  const handleCancelarEdicion = () => {
    setEditId(null);
    setEditData(null);
    clearFeedback();
    setTab('listado');
    navigate('/facturas', { replace: true });
  };

  const handleGuardarEdicion = async (facturaEditada) => {
    setLoading(true);
    clearFeedback();
    try {
      const facturaAntes = facturas.find(f => f._id === editId);
      const res = await secureFetch(`/api/invoices/${editId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(facturaEditada),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al editar el registro de venta');
      reportSuccess('Registro de venta actualizado correctamente.');
      setEditId(null);
      setEditData(null);
      registrarActividad(
        'Editar registro',
        `Registro N°${facturaAntes?.numero} actualizado\n${obtenerCambiosFactura(facturaAntes, facturaEditada)}`
      );
      fetchFacturas();
      setTab('listado');
      navigate('/facturas', { replace: true });
      return true;
    } catch (err) {
      reportError(err.message || 'Error de conexión');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleAgregarFactura = async (nuevaFactura) => {
    setLoading(true);
    clearFeedback();
    try {
      const res = await secureFetch("/api/invoices", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(nuevaFactura),
      });
      const data = await res.json();
      if (!res.ok) {
        // Si el error es por número repetido, mostrar mensaje especial
        if (data.code === 'INVOICE_NUMBER_DUPLICATE' || data.message?.includes('Ya existe un registro con ese número')) {
          reportError('El número de registro ya está en uso. Elegí otro número.');
        } else if (data.code === 'PLAN_DOCUMENT_LIMIT_REACHED') {
          reportError(
            data.message || 'Alcanzaste el límite de registros de tu plan actual.',
            { code: 'PLAN_DOCUMENT_LIMIT_REACHED' },
          );
        } else {
          reportError(data.message || 'Error al agregar registro');
        }
        return false;
      }
      const quickSale = isQuickSale(nuevaFactura);
      const createdSale = {
        ...data,
        documentType: data.documentType || nuevaFactura.documentType,
        metadata: data.metadata || nuevaFactura.metadata,
      };
      reportSuccess(quickSale
        ? 'Venta rápida registrada correctamente.'
        : 'Registro de venta agregado correctamente.');
      setLastCreatedSale(createdSale);
      setNuevaFacturaId(quickSale ? null : data._id);
      setNuevaAdjuntoInfo(quickSale ? null : (data.adjunto || null));
      if (nuevaFactura.recurrente) {
        try {
          const recRes = await secureFetch('/api/recurring', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              tipo: 'venta',
              nextRunDate: nuevaFactura.proximaFecha || defaultNextRun(nuevaFactura.fecha),
              payload: { ...nuevaFactura, recurrente: undefined, proximaFecha: undefined },
            }),
          });
          if (recRes.ok) {
            reportSuccess('Registro de venta agregado y plantilla mensual creada.');
          }
        } catch {
          // el registro ya se guardó
        }
      }
      fetchFacturas();
      if (quickSale) {
        setQuickFormResetKey((k) => k + 1);
        setTab('crear');
        navigate('/facturas?tab=crear&modo=rapido', { replace: true });
      } else {
        setTab('listado');
        navigate('/facturas', { replace: true });
      }
      return true;
    } catch (err) {
      reportError(err.message || 'Error de conexión');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Limpiar error al cambiar de pestaña o abrir el formulario de nuevo
  useEffect(() => {
    clearFeedback();
  }, [tab, clearFeedback]);

  const subirAdjuntoNueva = async () => {
    if (!nuevaFacturaId || !nuevaAdjunto) return;
    if (!nuevaFacturaId) {
      console.error('nuevaFacturaId es undefined');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('adjunto', nuevaAdjunto);
      const res = await secureFetch(`/api/invoices/${nuevaFacturaId}/adjunto`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'No se pudo subir el adjunto');
      setNuevaAdjuntoInfo(data.adjunto);
      setNuevaAdjunto(null);
      if (nuevaFileInputRef.current) nuevaFileInputRef.current.value = '';
      reportSuccess('Adjunto subido correctamente.');
      fetchFacturas();
    } catch (err) {
      reportError(err.message || 'Error al subir el adjunto');
    }
  };

  const descargarAdjuntoNueva = () => {
    if (!nuevaFacturaId || !nuevaAdjuntoInfo) return;
    if (!nuevaFacturaId) {
      console.error('nuevaFacturaId es undefined');
      return;
    }
    window.open(`/api/invoices/${nuevaFacturaId}/adjunto`, '_blank');
  };

  const eliminarAdjuntoNueva = async () => {
    if (!nuevaFacturaId || !nuevaAdjuntoInfo) return;
    if (!nuevaFacturaId) {
      console.error('nuevaFacturaId es undefined');
      return;
    }
    try {
      const res = await secureFetch(`/api/invoices/${nuevaFacturaId}/adjunto`, {
        method: 'DELETE',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'No se pudo eliminar el adjunto');
      setNuevaAdjuntoInfo(null);
      reportSuccess('Adjunto eliminado.');
    } catch (err) {
      reportError(err.message || 'Error al eliminar el adjunto');
    }
  };

  const descargarPDF = async (id, numero) => {
    if (!id) {
      console.error('ID de factura es undefined');
      return;
    }
    try {
      // Generar PDF directamente sin plantilla personalizada
      console.log('Generando PDF con plantilla por defecto');
      
      const res = await secureFetch(`/api/invoices/${id}/pdf`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ template: null })
      });
      
      if (!res.ok) throw new Error('No se pudo generar el PDF');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `comprobante_${numero}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      reportError(err.message || 'Error al descargar el PDF');
    }
  };

  const cambiarEstadoPago = async (id, estadoPago) => {
    const invoiceKey = id != null ? String(id) : '';
    if (!invoiceKey) {
      console.error('ID de factura es undefined');
      return;
    }
    setLoading(true);
    clearFeedback();
    try {
      const res = await secureFetch(`/api/invoices/${invoiceKey}/estado-pago`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ estadoPago }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Error al cambiar estado de pago");
      const normalized = normalizeInvoiceFromApi(data);
      setFacturas((prev) => patchInvoiceInList(prev, invoiceKey, normalized));
      setDetalle((prev) => (prev && sameInvoiceId(prev, invoiceKey) ? normalized : prev));
      reportSuccess('Estado de pago actualizado.');
      await fetchFacturas();
    } catch (err) {
      reportError(err.message || 'Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const importarMercadoPago = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setImportingMp(true);
    clearFeedback();
    try {
      const csv = await file.text();
      const previewRes = await secureFetch('/api/invoices/import/mercadopago/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csv }),
      });
      const preview = await previewRes.json();
      if (!previewRes.ok) throw new Error(preview.message || 'No se pudo leer el CSV');

      const count = preview.count || 0;
      if (!count) {
        reportError('No encontramos cobros importables en ese CSV.');
        return;
      }

      const matched = preview.matchedCount || 0;
      const unmatched = preview.unmatchedCount ?? count;
      const ok = window.confirm(
        matched
          ? `Encontramos ${count} cobro(s) de Mercado Pago.\n\n${matched} coinciden con impagas (se marcan cobradas).\n${unmatched} se importan como ventas pagadas.\n\n¿Aplicar?`
          : `Encontramos ${count} cobro(s) de Mercado Pago.\n\n¿Importarlos como ventas pagadas?`
      );
      if (!ok) return;

      const importRes = await secureFetch('/api/invoices/import/mercadopago', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: preview.suggestions || [] }),
      });
      const data = await importRes.json();
      if (!importRes.ok) throw new Error(data.message || 'Error al importar');

      reportSuccess(data.message || `Importadas ${data.created || 0} ventas.`);
      fetchFacturas();
    } catch (err) {
      reportError(err.message || 'Error al importar Mercado Pago');
    } finally {
      setImportingMp(false);
    }
  };

  const exportarVentasExcel = async () => {
    setExportingCsv(true);
    clearFeedback();
    try {
      const name = await downloadTabularExport(
        secureFetch,
        '/api/export/ventas',
        'contsuite-ventas.xlsx'
      );
      reportSuccess(`Exportación descargada (${name}).`);
    } catch (err) {
      reportError(err.message || 'Error al exportar');
    } finally {
      setExportingCsv(false);
    }
  };

  const enviarFacturaPorEmail = async (id) => {
    setLoading(true);
    clearFeedback();
    let result = { ok: false, message: '' };
    try {
      const res = await secureFetch(`/api/invoices/${id}/send-email`, {
        method: 'POST'
      });
      let data = {};
      const contentType = res.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await res.json();
      } else {
        const text = await res.text();
        throw new Error('El servidor devolvió una respuesta inesperada.\n' + text);
      }
      if (!res.ok) throw new Error(data.message || 'Error al enviar el email');
      result = { ok: true, message: 'Comprobante enviado por email correctamente.' };
    } catch (err) {
      result = { ok: false, message: err.message || 'Error al enviar el email' };
    } finally {
      setLoading(false);
    }
    return publishActionResult(result);
  };

  const enviarRecordatorioPago = async (id) => {
    setLoading(true);
    clearFeedback();
    let result = { ok: false, message: '' };
    try {
      const res = await secureFetch(`/api/invoices/${id}/reminder`, {
        method: 'POST'
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.code === 'PROFESIONAL_REQUIRED') {
          throw new Error('Los recordatorios de cobranza están disponibles en el plan Profesional.');
        }
        throw new Error(data.message || 'Error al enviar el recordatorio');
      }
      const message = data.message || 'Recordatorio de pago enviado correctamente.';
      result = { ok: true, message };
      fetchFacturas();
    } catch (err) {
      result = { ok: false, message: err.message || 'Error al enviar el recordatorio' };
    } finally {
      setLoading(false);
    }
    return publishActionResult(result);
  };

  const enviarRecordatoriosMasivos = async () => {
    if (!impagasElegibles.length) return;
    const ok = window.confirm(
      `¿Enviar recordatorio de pago a ${impagasElegibles.length} cliente(s) con comprobantes impagos?\n\nSe omitirán las ventas sin email.`
    );
    if (!ok) return;

    setBulkRemindersSending(true);
    clearFeedback();
    try {
      const res = await secureFetch('/api/invoices/reminders/bulk', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) {
        if (data.code === 'PROFESIONAL_REQUIRED') {
          throw new Error('Los recordatorios de cobranza están disponibles en el plan Profesional.');
        }
        throw new Error(data.message || 'Error al enviar recordatorios');
      }
      const omitidas = data.skipped || 0;
      reportSuccess(
        `${data.message}${omitidas ? ` (${omitidas} omitida(s) sin email).` : ''}`,
      );
    } catch (err) {
      reportError(err.message || 'Error al enviar recordatorios');
    } finally {
      setBulkRemindersSending(false);
    }
  };

  const agregarPago = async (id, payload) => {
    const invoiceKey = id != null ? String(id) : '';
    if (!invoiceKey) throw new Error('Comprobante sin identificador.');
    try {
      const res = await secureFetch(`/api/invoices/${invoiceKey}/pagos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al registrar el pago');
      const normalized = normalizeInvoiceFromApi(data);
      setFacturas((prev) => patchInvoiceInList(prev, invoiceKey, normalized));
      setDetalle((prev) => (prev && sameInvoiceId(prev, invoiceKey) ? normalized : prev));
      return publishActionResult({ ok: true, message: 'Pago registrado.' });
    } catch (err) {
      publishActionResult({
        ok: false,
        message: err.message || 'Error al registrar el pago',
      });
      throw err;
    }
  };

  const agregarGestion = async (id, payload) => {
    const invoiceKey = id != null ? String(id) : '';
    if (!invoiceKey) throw new Error('Comprobante sin identificador.');
    try {
      const res = await secureFetch(`/api/invoices/${invoiceKey}/gestiones`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al guardar la gestión');
      const normalized = normalizeInvoiceFromApi(data);
      setFacturas((prev) => patchInvoiceInList(prev, invoiceKey, normalized));
      setDetalle((prev) => (prev && sameInvoiceId(prev, invoiceKey) ? normalized : prev));
      return publishActionResult({ ok: true, message: 'Gestión registrada.' });
    } catch (err) {
      publishActionResult({
        ok: false,
        message: err.message || 'Error al guardar la gestión',
      });
      throw err;
    }
  };

  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-600';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const inputClass = modoOscuro ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-gray-500 focus:border-gray-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500';
  const tabInactive = modoOscuro ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50';
  const btnOut = modoOscuro ? 'border-gray-600 text-white hover:bg-gray-700' : 'border-gray-300 text-gray-900 hover:bg-gray-50';
  const divide = modoOscuro ? 'border-gray-700' : 'border-gray-200';
  const inputBg = modoOscuro
    ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500'
    : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400';
  const pillIdle = modoOscuro ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-600 border border-gray-200';
  const heroBg = modoOscuro
    ? 'bg-gradient-to-br from-[#07192f] to-gray-900 border-[#1F80FF]/30'
    : 'bg-gradient-to-br from-blue-50 to-white border-blue-200';
  const showPostCreateActions = Boolean(
    lastCreatedSale
    && (success.startsWith('Venta rápida registrada') || success.startsWith('Registro de venta agregado'))
  );
  const quickCreateActive = tab === 'crear' && createMode === 'quick' && editId === null && !editData;

  return (
    <div className={modoOscuro ? 'w-full min-h-[calc(100dvh-4rem)] bg-black text-white' : 'w-full min-h-[calc(100dvh-4rem)] bg-[#f5f5f7] text-gray-900'}>
      <div className={`w-full max-w-[85rem] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-12 py-3 space-y-2 ${quickCreateActive ? 'pb-24 sm:pb-3' : 'pb-3'}`}>
        <header className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className={`text-xl sm:text-2xl font-semibold leading-tight ${textPri}`}>Ventas</h1>
            {isFetching && (
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current opacity-60" />
            )}
          </div>
          {!quickCreateActive && (
            <p className={`text-xs sm:text-sm mt-0.5 ${textSec}`}>
              Registrá cobros rápidos o completá todos los datos de una venta.
            </p>
          )}
        </header>

        <PageFeedbackBlock
          success={success}
          error={error}
          successAction={
            showPostCreateActions && (tab === 'listado' || quickCreateActive) ? (
              <div className="flex w-full flex-wrap gap-2 sm:w-auto">
                <OnboardingContinueAction
                  stepId="sale"
                  visible={true}
                  modoOscuro={modoOscuro}
                />
                <button
                  type="button"
                  onClick={() => setDetalle(lastCreatedSale)}
                  className={`flex-1 rounded-lg border px-3 py-1.5 text-xs font-semibold sm:flex-none ${btnOut}`}
                >
                  Ver detalle
                </button>
                <button
                  type="button"
                  onClick={() => goToCreate('quick')}
                  className="flex-1 rounded-lg bg-[#1F80FF] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#004AAD] sm:flex-none"
                >
                  Cargar otra
                </button>
              </div>
            ) : null
          }
          errorAction={
            errorCode === 'PLAN_DOCUMENT_LIMIT_REACHED' ? (
              <button
                type="button"
                onClick={() => navigate('/uso-plan')}
                className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shrink-0"
              >
                Ampliar uso
              </button>
            ) : null
          }
        />

        <div className={`rounded-xl border p-0.5 ${box}`}>
          <div className="flex gap-1">
            {[
              { id: 'listado', label: 'Mis ventas' },
              ...(readOnly ? [] : [
                { id: 'crear', label: editId !== null ? 'Editar' : 'Cargar venta' },
                { id: 'recurrentes', label: 'Recurrentes' },
              ]),
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => switchMainTab(t.id)}
                className={`flex-1 px-3 py-1.5 rounded-lg text-sm font-medium ${
                  tab === t.id ? 'bg-[#1F80FF] text-white' : tabInactive
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

      {tab === 'listado' && !readOnly && (
        <FacturasListQuickCta
          heroBg={heroBg}
          textPri={textPri}
          textSec={textSec}
          modoOscuro={modoOscuro}
          btnOut={btnOut}
          onQuickSale={() => goToCreate('quick')}
          onCompleteSale={() => goToCreate('complete')}
        />
      )}
      {tab === 'listado' && (
        <FacturasListPanel
          readOnly={readOnly}
          modoOscuro={modoOscuro}
          box={box}
          textPri={textPri}
          textSec={textSec}
          textMuted={textMuted}
          divide={divide}
          btnOut={btnOut}
          inputBg={inputBg}
          pillIdle={pillIdle}
          kpis={kpis}
          totalesPorMoneda={totalesPorMoneda}
          totalEquivalenteArs={totalEquivalenteArs}
          facturasFiltradas={facturasFiltradas}
          facturas={facturas}
          clientesUnicos={clientesUnicos}
          busqueda={busqueda}
          handleBusqueda={handleBusqueda}
          filtroCliente={filtroCliente}
          setFiltroCliente={setFiltroCliente}
          filtroFechaDesde={filtroFechaDesde}
          setFiltroFechaDesde={setFiltroFechaDesde}
          filtroFechaHasta={filtroFechaHasta}
          setFiltroFechaHasta={setFiltroFechaHasta}
          filtroEstado={filtroEstado}
          setFiltroEstado={setFiltroEstado}
          filtroAging={filtroAging}
          setFiltroAging={setFiltroAging}
          filtroCategoria={filtroCategoria}
          setFiltroCategoria={setFiltroCategoria}
          setBusqueda={setBusqueda}
          goToCreateQuick={() => goToCreate('quick')}
          handleDuplicarUltima={handleDuplicarUltima}
          fetchFacturas={fetchFacturas}
          isFetching={isFetching}
          exportarVentasExcel={exportarVentasExcel}
          exportingCsv={exportingCsv}
          hasProfesional={hasProfesional}
          mpFileInputRef={mpFileInputRef}
          importarMercadoPago={importarMercadoPago}
          importingMp={importingMp}
          impagasElegibles={impagasElegibles}
          enviarRecordatoriosMasivos={enviarRecordatoriosMasivos}
          bulkRemindersSending={bulkRemindersSending}
          openDetalle={openDetalle}
          handleEditar={handleEditar}
          handleDuplicar={handleDuplicar}
          descargarPDF={descargarPDF}
          cambiarEstadoPago={cambiarEstadoPago}
          menuOpenId={menuOpenId}
          setMenuOpenId={setMenuOpenId}
          handleEliminar={handleEliminar}
          enviarFacturaPorEmail={enviarFacturaPorEmail}
          enviarRecordatorioPago={enviarRecordatorioPago}
          agregarGestion={agregarGestion}
          whatsappOptions={whatsappOptions}
        />
      )}
        
      {!readOnly && tab === 'recurrentes' && (
        <RecurringList
          tipo="venta"
          modoOscuro={modoOscuro}
          onGenerated={() => {
            reportSuccess('Venta generada desde la plantilla.');
            fetchFacturas();
          }}
        />
      )}
      {!readOnly && tab === 'crear' && (
        <FacturasCreatePanel
          box={box}
          divide={divide}
          textPri={textPri}
          textMuted={textMuted}
          tabInactive={tabInactive}
          editId={editId}
          editData={editData}
          createMode={createMode}
          setCreateMode={setCreateMode}
          setError={setError}
          quickFormResetKey={quickFormResetKey}
          onAdd={handleAgregarFactura}
          onSaveEdit={handleGuardarEdicion}
          onCancelEdit={handleCancelarEdicion}
          modoOscuro={modoOscuro}
          facturas={facturas}
          preferImpagaOnboarding={onboardingFlow}
        />
      )}
      {nuevaFacturaId && (
          <div className={`rounded-xl border p-4 w-full ${box}`}>
            <div className={`mb-2 text-sm font-semibold ${textPri}`}>Adjuntar archivo a la nueva factura</div>
          <input
            type="file"
            ref={nuevaFileInputRef}
            className={`block w-full border rounded-lg p-2 ${inputClass}`}
            onChange={e => setNuevaAdjunto(e.target.files[0])}
            accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx,.zip,.rar"
          />
          <button
            type="button"
            className="mt-2 px-4 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            disabled={!nuevaAdjunto}
            onClick={subirAdjuntoNueva}
          >
            Subir adjunto
          </button>
          {nuevaAdjuntoInfo && (
            <div className="mt-2 flex items-center gap-3">
              <span className={`text-sm font-semibold truncate max-w-xs ${modoOscuro ? 'text-green-300' : 'text-green-700'}`}>{nuevaAdjuntoInfo}</span>
              <button type="button" className={modoOscuro ? 'text-blue-400 underline' : 'text-blue-600 underline'} onClick={descargarAdjuntoNueva}>Descargar</button>
              <button type="button" className={modoOscuro ? 'text-red-400 underline' : 'text-red-600 underline'} onClick={eliminarAdjuntoNueva}>Eliminar</button>
            </div>
          )}
        </div>
      )}
        
      <FacturaDetalleModal
        factura={detalle}
        onClose={() => setDetalle(null)}
        modoOscuro={modoOscuro}
        onEnviarEmail={enviarFacturaPorEmail}
        onEnviarRecordatorio={enviarRecordatorioPago}
        onAddGestion={agregarGestion}
        onAddPago={agregarPago}
        hasProfesional={hasProfesional}
        readOnly={readOnly}
        whatsappOptions={whatsappOptions}
      />
      </div>
    </div>
  );
};

export default Facturas; 